import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { ErrorBag } from 'src/models/ErrorModel';
import { FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { FORM_ID } from 'src/models/FormModel';
import { useCallback, useEffect, useMemo } from 'react';
import { getAmbassadorListAction } from 'src/redux/actions/ambassadorActions';
import { t } from 'src/lib/language';
import Row from 'src/components/UI/Row';
import { openModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';

const useStyles = makeStyles(() => ({
  formControl: {
    minWidth: '200px',
    marginTop: '4px',
    width: '100%',
  },
  link: {
    color: '#0A4DF2',
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    marginBottom: 0,
  },
  link_disabled: {
    color: '#454444',
    marginBottom: 0,
  },
}));

interface Props {
  disabled?: boolean;
  form: FORM_ID;
  fundraisingId: number | string;
  fundraisingSlug?: string;
  label: string;
  name: string;
  onChange: (id: string | number) => void;
  showEdit?: boolean;
  value?: string | number;
}

interface AmbassadorItem {
  id: number | string;
  label?: string;
}

interface Ambassador {
  id: number | string;
  name: string;
}

const AmbassadorSelector = (props: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const {
    disabled,
    form,
    fundraisingId,
    fundraisingSlug,
    label,
    name,
    onChange,
    showEdit,
    value,
  } = props;

  useEffect(() => {
    dispatch(getAmbassadorListAction(fundraisingId));
  }, [dispatch, fundraisingId]);

  const ambassadors = useSelector((state: Store) => state.ambassadors) as Ambassador[];
  const errors: ErrorBag = useSelector((state: Store) => state.errors[form] || {} as ErrorBag);

  const options = useMemo(() => {
    const items: AmbassadorItem[] = [{ id: 0, label: undefined }];
    if (!ambassadors.length) return items;

    return items.concat(
      ambassadors
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item) => ({
          id: item.id,
          label: item.name,
        })),
    );
  }, [ambassadors]);

  const selectedValue = useMemo(() => {
    if (!ambassadors.length || !value) {
      return { id: 0, label: undefined } as AmbassadorItem;
    }
    const ambassador = ambassadors.find((item) => item.id === value);
    return ambassador
      ? { id: ambassador.id, label: `${ambassador.id} ${ambassador.name}` }
      : { id: 0, label: undefined };
  }, [ambassadors, value]);

  const handleValueChange = useCallback((item: AmbassadorItem | null) => {
    if (!item || !item.id) {
      onChange(0);
    } else {
      onChange(item.id);
    }
  }, [onChange]);

  const getOptionLabel = useCallback((option: AmbassadorItem) => option.label || '', []);

  const filterOptions = useCallback((optionList: AmbassadorItem[], state: any) => {
    const { inputValue } = state;
    if (inputValue.length <= 3) {
      return optionList; // Show all when ≤ 3 chars
    }
    return optionList.filter(
      (option) => option.label?.toLowerCase().includes(inputValue.toLowerCase()),
    );
  }, []);

  const onClickEdit = () => {
    dispatch(openModal({ modal: MODALS.editAmbassador, payload: { id: value, fundraisingSlug } }));
  };

  return (
    <>
      <FormControl
        variant="outlined"
        className={classes.formControl}
        key={`form-control-${name}`}>
        <Autocomplete
          disabled={disabled}
          value={selectedValue}
          onChange={(_, item) => handleValueChange(item)}
          options={options}
          filterOptions={filterOptions}
          getOptionLabel={getOptionLabel}
          getOptionSelected={(option, item) => option.id === item.id}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={label}
              error={!!errors[name]}
              placeholder="Type 4+ chars to search ambassadors..." />
          )}
          key={`autocomplete-${name}`} />
      </FormControl>
      { showEdit && fundraisingSlug && (
        <Row>
          <div
            className={disabled ? classes.link_disabled : classes.link}
            style={{ marginRight: '10px' }}
            onClick={() => onClickEdit()}>
            <i>{t(lang, 'menus.edit_ambassador')}</i>
          </div>
        </Row>
      )}
    </>
  );
};

export default AmbassadorSelector;
